import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import Api from '../../api';
import { assignSupervisor, removeSupervisor } from '../../api/supervisor';
import { secondaryColors } from '../../theme/themeConfig';
import useNotificationsStore from '../../store/zustand/notifications';

interface CurrentSupervisor {
  firstName: string;
  id: string;
  lastName: string;
}

interface Props {
  currentSupervisor?: CurrentSupervisor | null;
  currentSupervisorRate?: number;
  onChanged: (supervisor: CurrentSupervisor | null, rate?: number) => void;
  projectId: string;
}

const SupervisorAssignWidget: React.FC<Props> =
  function SupervisorAssignWidget({
    currentSupervisor,
    currentSupervisorRate,
    onChanged,
    projectId
  }) {
    const { addNotification } = useNotificationsStore((s) => s);
    const [open, setOpen] = useState(false);
    const [supervisors, setSupervisors] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState(currentSupervisor?.id ?? '');
    const [rate, setRate] = useState(
      currentSupervisorRate ? String(currentSupervisorRate) : ''
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      if (!open) return;
      setSelectedId(currentSupervisor?.id ?? '');
      setRate(currentSupervisorRate ? String(currentSupervisorRate) : '');
      setLoading(true);
      Api.UserResocesApi.getUsersByRole({ roles: ['Supervisor'] })
        .then((d: any) => setSupervisors(Array.isArray(d) ? d : d?.data ?? []))
        .catch(() => setSupervisors([]))
        .finally(() => setLoading(false));
    }, [open]);

    const handleSupervisorSelect = (id: string) => {
      setSelectedId(id);
      const sup = supervisors.find((s) => s.id === id);
      const rawRate = sup?.rateNumber ?? sup?.rate;
      if (rawRate != null && rawRate !== '' && rawRate !== 'not rate') {
        setRate(String(Number(rawRate)));
      }
    };

    const handleAssign = async () => {
      if (!selectedId) return;
      setSaving(true);
      try {
        await assignSupervisor(projectId, {
          supervisorId: selectedId,
          supervisorRate: rate ? parseFloat(rate) : undefined
        });
        const sup = supervisors.find((s) => s.id === selectedId);
        onChanged(
          sup
            ? { firstName: sup.firstName, id: sup.id, lastName: sup.lastName }
            : null,
          rate ? parseFloat(rate) : undefined
        );
        addNotification('Supervisor assigned', 'success');
        setOpen(false);
        setSelectedId('');
        setRate('');
      } catch {
        addNotification('Failed to assign supervisor', 'error');
      } finally {
        setSaving(false);
      }
    };

    const handleRemove = async () => {
      setSaving(true);
      try {
        await removeSupervisor(projectId);
        onChanged(null);
        addNotification('Supervisor removed', 'success');
      } catch {
        addNotification('Failed to remove supervisor', 'error');
      } finally {
        setSaving(false);
      }
    };

    return (
      <>
        {currentSupervisor ? (
          /* ── Assigned state — inline, no heavy buttons ── */
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Box>
              <Typography fontSize={13} fontWeight={500}>
                {currentSupervisor.firstName} {currentSupervisor.lastName}
              </Typography>
              {currentSupervisorRate ? (
                <Typography
                  color={secondaryColors.SECONDARY}
                  fontSize={11}
                  mt={0.2}
                >
                  ${currentSupervisorRate}/hr
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
              <IconButton
                onClick={() => setOpen(true)}
                size="small"
                sx={{ color: secondaryColors.SECONDARY, p: '4px' }}
                title="Change supervisor"
              >
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <Typography
                onClick={saving ? undefined : handleRemove}
                sx={{
                  '&:hover': { color: '#94A3B8' },
                  color: '#CBD5E1',
                  cursor: saving ? 'default' : 'pointer',
                  fontSize: 11,
                  px: 0.5
                }}
              >
                Remove
              </Typography>
            </Box>
          </Box>
        ) : (
          /* ── Unassigned state — subtle text link ── */
          <Box
            onClick={() => setOpen(true)}
            sx={{
              '&:hover': { color: '#005DA2' },
              alignItems: 'center',
              color: secondaryColors.SECONDARY,
              cursor: 'pointer',
              display: 'inline-flex',
              gap: 0.5
            }}
          >
            <PersonAddAltOutlinedIcon sx={{ fontSize: 14 }} />
            <Typography fontSize={12}>Assign supervisor</Typography>
          </Box>
        )}

        <Dialog
          fullWidth
          maxWidth="xs"
          onClose={() => setOpen(false)}
          open={open}
        >
          <DialogTitle>
            {currentSupervisor ? 'Change Supervisor' : 'Assign Supervisor'}
          </DialogTitle>
          <DialogContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
              >
                <Box>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={12}
                    fontWeight={600}
                    mb={0.5}
                  >
                    Supervisor
                  </Typography>
                  <Select
                    displayEmpty
                    fullWidth
                    onChange={(e) => handleSupervisorSelect(e.target.value)}
                    size="small"
                    value={selectedId}
                  >
                    <MenuItem disabled value="">
                      Select a supervisor
                    </MenuItem>
                    {supervisors.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.firstName} {s.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={12}
                    fontWeight={600}
                    mb={0.5}
                  >
                    Hourly rate ($)
                  </Typography>
                  <TextField
                    fullWidth
                    inputProps={{ min: 0, step: 0.01 }}
                    onChange={(e) => setRate(e.target.value)}
                    placeholder="e.g. 25.00"
                    size="small"
                    type="number"
                    value={rate}
                  />
                </Box>
                {currentSupervisor && (
                  <Box
                    sx={{
                      background: '#FFF3CD',
                      border: '1px solid #FFC107',
                      borderRadius: '8px',
                      p: '10px 12px'
                    }}
                  >
                    <Typography sx={{ color: '#856404', fontSize: 12 }}>
                      Changing the supervisor will update the rate on this
                      project and affect margin calculations going forward.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ pb: 2, px: 3 }}>
            <Button onClick={() => setOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              disabled={!selectedId || saving}
              onClick={handleAssign}
              variant="contained"
            >
              {saving ? (
                <CircularProgress color="inherit" size={18} />
              ) : (
                'Assign'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

SupervisorAssignWidget.defaultProps = {
  currentSupervisor: undefined,
  currentSupervisorRate: undefined
};

export default SupervisorAssignWidget;
